import {evidenceScore} from '../../shared/evidenceScore'
import {
  DWS_SLOTS, DWS_IMAGE_TYPES, DWS_VIDEO_TYPES,
  DWS_MAX_IMAGE_BYTES, DWS_MAX_VIDEO_BYTES, mb, ext,
} from '../data/dwsCopy'
import {PACKS} from '../data/noberoFreeze'
import {NOW} from '../data/reference'
import {getVolumetricDivisor} from '../weightMath'

/**
 * Pure logic behind the freeze Evidence Studio. Everything here is a function
 * of the form's current values — the drawer holds the state, this holds the
 * rules, and the tests exercise the rules without mounting anything.
 *
 * Session uploads are NOT PERSISTED. There is no API; a record built here
 * lands in the shared store (see state/useFreezeStore.js's own `uploads`
 * array, via its `addUpload` action) and is gone on reload.
 */
export const MISSING = {
  SKU: 'SKU', EVIDENCE: 'EVIDENCE', AWB: 'AWB', COURIER: 'COURIER', WEIGHT: 'WEIGHT',
}

/**
 * Typed keys in a fixed order, NOT an assembled English sentence. The mockup
 * joined gap phrases with ', ' and used the result verbatim as footer text,
 * tooltip and aria-label at once, which breaks silently under i18n and under
 * any copy edit. Render these through MISSING_LABEL, separately.
 */
export function uploadMissing({row, video, images, awb, courier, kg}) {
  const out = []
  if (!row) out.push(MISSING.SKU)
  if (!video && Object.keys(images || {}).length === 0) out.push(MISSING.EVIDENCE)
  if (!String(awb || '').trim()) out.push(MISSING.AWB)
  if (!courier) out.push(MISSING.COURIER)
  if (!(Number(kg) > 0)) out.push(MISSING.WEIGHT)
  return out
}

/**
 * What is still worth adding, most valuable first — computed as the actual
 * delta the same scoring function returns, so the number on a chip is exactly
 * what pressing it earns. Never a hard-coded table.
 */
export function uploadTodo({video, images, awb, courier, note}) {
  const filled = Object.keys(images || {}).length
  const identified = !!(String(awb || '').trim() && courier)
  const remark = !!String(note || '').trim()
  const base = evidenceScore({video: !!video, imageSlots: filled, category: identified, remark}).score
  const gain = (patch) => evidenceScore({
    video: !!video, imageSlots: filled, category: identified, remark, ...patch,
  }).score - base

  const todo = []
  if (!video) todo.push({key: 'video', label: 'Add a packing video', big: true, gain: gain({video: true})})
  DWS_SLOTS.forEach((slot) => {
    if ((images || {})[slot.k]) return
    todo.push({key: `img:${slot.k}`, label: `Add ${slot.label.toLowerCase()} image`, big: false, gain: gain({imageSlots: filled + 1})})
  })
  if (!identified) todo.push({key: 'identify', label: 'Identify the shipment', big: false, gain: gain({category: true})})
  return todo
}

function validate(file, types, max, kindWords) {
  if (!file) return {ok: false, message: ''}
  if (!types[file.type]) return {ok: false, message: `This is a ${ext(file.name)} file. Upload ${kindWords}.`}
  if (file.size > max) return {ok: false, message: `${mb(file.size)} is over the ${mb(max)} limit for one ${kindWords.includes('clip') ? 'clip' : 'image'}.`}
  if (!file.size) return {ok: false, message: 'This file is empty.'}
  return {ok: true, message: ''}
}

export function validateImage(file) {
  return validate(file, DWS_IMAGE_TYPES, DWS_MAX_IMAGE_BYTES, 'a JPG, PNG or WebP image')
}

export function validateVideo(file) {
  return validate(file, DWS_VIDEO_TYPES, DWS_MAX_VIDEO_BYTES, 'an MP4, MOV or WebM clip')
}

function round2(n) {
  return Math.round(n * 100) / 100
}

/**
 * The one shape a captured video-and-stills set takes when it leaves a form.
 * Two callers now hold the same five slots — the Evidence Studio, whose set
 * becomes one DWS record below, and Initiate's raise drawer, whose set travels
 * on the freeze request itself — and a second shaper would let the two drift
 * on the very field a reviewer opens the record to look at.
 *
 * Slot order follows DWS_SLOTS rather than the merchant's fill order, so two
 * identical sets recorded in a different sequence read the same afterwards.
 */
/**
 * The File objects the drawers hold beside their preview urls, for the live multipart upload
 * (freeze-v2/data/api.js#apiUploadEvidence). Placeholders without a `file` (seeded fixtures,
 * the harness) simply yield nothing to send.
 */
export function rawEvidenceFiles({video, images}) {
  const held = images || {}
  return {
    video: video && video.file ? video.file : null,
    images: Object.keys(held).map((k) => held[k] && held[k].file).filter(Boolean),
  }
}

export function buildEvidenceFiles({video, images}) {
  const held = images || {}
  return {
    video: video ? {name: video.name, url: video.url} : null,
    images: DWS_SLOTS
      .filter((slot) => held[slot.k])
      .map((slot) => ({slot: slot.k, name: held[slot.k].name, url: held[slot.k].url})),
  }
}

/**
 * Mint one evidence record: the video, the images and the readings for a
 * SINGLE shipment, which is the unit Prozo reviews.
 *
 * `seq` is a monotonic session counter, not a timestamp — the app clock is
 * pinned at NOW and Date.now() is banned module-wide. The caller derives
 * `seq` from the store's own `uploads.length + 1` (see DwsPage.js) rather
 * than a ref-held counter, because the store — not this page — is what
 * survives a rail-click unmount; a component-local counter would restart at
 * 1 and collide with an id already on file the next time this section mounts.
 *
 * Dimensions: typed values win. Otherwise fall back to the row's packing
 * material, and if that material is a flyer (dims: null) write dims: null and
 * vol: 0 rather than inventing a box. A fabricated dimension triple on an
 * evidence record is exactly the kind of thing this module refuses to do.
 */
export function buildUploadRecord({row, awb, courier, kg, dim, note, video, images}, seq) {
  const l = Number(dim && dim.l); const b = Number(dim && dim.b); const h = Number(dim && dim.h)
  const typed = l > 0 && b > 0 && h > 0

  const pack = typed ? null : PACKS.find((p) => p.id === row.packId)
  const packDims = pack && pack.dims ? pack.dims.slice() : null
  const finalDims = typed ? [l, b, h] : packDims

  const vol = finalDims
    ? round2((finalDims[0] * finalDims[1] * finalDims[2]) / getVolumetricDivisor())
    : 0

  return {
    id: `DWS-UP-S${seq}`,
    sku: row.sku || row.key,
    awb: String(awb).trim(),
    courier,
    date: NOW.toISOString().slice(0, 10),
    read: Number(kg),
    dims: finalDims,
    vol,
    source: 'upload',
    ...buildEvidenceFiles({video, images}),
    note: String(note || '').trim() || null,
  }
}
